import React, { useEffect, useState } from 'react';
import { analytics } from '../../helpers/firebaseHelper';
import { AlertsWrapper } from './style';
import { GET_ALERTS } from '@core/ui/graphql/Alerts';
import { DEL_ALERT, UPDATE_ALERT } from '@core/ui/graphql/Mutations';
import { useMutation, useQuery } from '@apollo/client';
import { ReactTable } from '../Placeholder';
import Table from '../Table';
import Icon from 'react-icons-kit';
import { androidDelete } from 'react-icons-kit/ionicons/androidDelete';
import { confirmAlert } from 'react-confirm-alert';
import OnError from '../OnError';
import Empty from '../Empty';
import { useToasts } from 'react-toast-notifications';
import {
  RenderAction,
  RenderDate,
  renderTitle,
  RenderNotification,
} from '../Renderer/alerts';
import { useTranslation } from 'next-i18next';
import { SpinnerLoader } from '../Loader/spinnerLoader';
import { get, isEmpty, omit } from 'lodash';

const Alerts = ({ setCounter, withTitle, uid }) => {
  const { t } = useTranslation();

  let keys = [];

  const { addToast } = useToasts();
  const [alertData, setAlertData] = useState([]);
  const [alertsTable, setAlertsTable] = useState([]);
  const { data, loading, error } = useQuery(GET_ALERTS, {
    fetchPolicy: 'no-cache',
    variables: { LIMIT: 50, uid: uid },
  });

  const [deleteMutation] = useMutation(DEL_ALERT);
  const [updateMutation] = useMutation(UPDATE_ALERT);

  const { alerts } = data || {};

  useEffect(() => {
    if (alerts) {
      setAlertData(alerts.data);
    }
  }, [data]);

  useEffect(() => {
    if (!loading && !error && alertData instanceof Array) {
      setCounter(alertData.length);
      setAlertsTable(
        alertData.map(item => {
          return {
            key: item.id,
            title: renderTitle(item),
            notification: (
              <RenderNotification item={item} updateNotif={updateNotif} />
            ),
            createdAt: <RenderDate item={item} />,
            action: <RenderAction item={item} deleteAction={confirmAction} />,
          };
        })
      );
    }
  }, [alertData]);

  let totalTitle = null;
  if (!loading && !error && alerts && alerts.data.length > 0) {
    // TODO use i18n
    totalTitle = (
      <span>
        {alerts.data.length} sauvegardée
        {alerts.data.length > 1 ? 's' : ''}
      </span>
    );
  }

  const confirmAction = ids => {
    if (isEmpty(ids)) {
      addToast(t('profile.alerts.delete.empty'), {
        appearance: 'warning',
        autoDismiss: true,
      });
      return;
    }
    const isArray = ids instanceof Array;
    const msg =
      isArray && ids.length > 1
        ? 'profile.alerts.delete.confirm.plural'
        : 'profile.alerts.delete.confirm.singular';
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="custom-ui-delete-modal">
            <h3>Confirmation</h3>
            <p>{t(msg)}</p>
            <button onClick={onClose}>{t('cancel')}</button>
            <button
              onClick={async () => {
                await deleteAlert(isArray ? ids : [ids]);
                onClose();
              }}
            >
              {t('delete')}
            </button>
          </div>
        );
      },
    });
  };

  const deleteAlert = async ids => {
    const res = await deleteMutation({
      variables: {
        alert: { ids },
      },
    });
    if (get(res, 'data.deleteAlert.success', false)) {
      setAlertData(alertData.filter(alert => ids.indexOf(alert.id) === -1));
      const toastMessage =
        ids.length > 1
          ? 'profile.alerts.delete.confirm.success.plural'
          : 'profile.alerts.delete.confirm.success.singular';
      addToast(t(toastMessage), {
        appearance: 'success',
        autoDismiss: true,
      });

      if (typeof window !== 'undefined' && analytics) {
        // create analytics event
        ids.forEach(alert => {
          analytics.logEvent('alert_remove', {
            title: alert.title,
            filter: alert.filter,
          });
        });
      }
    } else {
      addToast(t('profile.alerts.delete.confirm.error'), {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  const updateNotif = async item => {
    setAlertData(
      alertData.map(alert => {
        if (alert.id === item.id) {
          alert = item;
        }
        return alert;
      })
    );
    const res = await updateMutation({
      variables: {
        data: omit(item, ['posts', 'uid', 'createdAt']),
      },
    });
    if (get(res, 'data.updateAlert.success', false)) {
      const msg = `${t('profile.alerts.update.success')} ${item.title}`;
      addToast(msg, { appearance: 'success', autoDismiss: true });
    } else {
      const msg = get(
        res,
        'data.updateAlert.message',
        'profile.alerts.update.error'
      );
      addToast(t(msg), { appearance: 'error', autoDismiss: true });
    }
  };

  const onSelect = ids => {
    keys = ids;
  };

  const columns = [
    {
      Header: (
        <span onClick={() => confirmAction(keys)} className="btn delete">
          <Icon size={25} mr={10} icon={androidDelete} /> {t('delete')}
        </span>
      ),
      accessor: 'title',
    },
    {
      Header: t('profile.alerts.notification.header'),
      accessor: 'notification',
    },
    {
      Header: t('creationDate'),
      accessor: 'createdAt',
    },
    {
      Header: t('actions'),
      accessor: 'action',
    },
  ];

  if (error && !loading) {
    return <OnError />;
  }

  return (
    <AlertsWrapper>
      {withTitle && (
        <h2>
          {t('header.alerts.label')} : {totalTitle}
        </h2>
      )}
      <div className="alerts-list">
        {loading ? (
          <>
            <ReactTable />
            <SpinnerLoader className="mobile" />
          </>
        ) : !loading && get(data, 'alerts.total', 0) === 0 ? (
          <Empty
            title={t('profile.alerts.empty.title')}
            description={t('profile.alerts.empty.description')}
          />
        ) : (
          <Table
            className="responsive"
            columns={columns}
            data={alertsTable}
            onSelect={onSelect}
          />
        )}
      </div>
    </AlertsWrapper>
  );
};

export default Alerts;
